import axios from 'axios';
import { getBaseUrl, getAuthCredentials } from './api';
import type { BugReportPayload, BugReportResult } from '@/types';

// Endpoint-ul backend care creează issue-ul în Gitea.
const BUG_REPORT_ENDPOINT = '/auth/data/support/bug-report';

// Limite ținute sincron cu validarea din auth-server (server.js).
export const SUPPORT_MAX_FILES = 5;
export const SUPPORT_MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
export const SUPPORT_MAX_TITLE_LENGTH = 150;
export const SUPPORT_MAX_DESCRIPTION_LENGTH = 5000;

// Extrage mesajul util din răspunsul serverului. Backend-ul întoarce
// { error: '...' } pe 400/429/502/503; pe erorile trecute prin handleApiError
// mesajul generic e în .message, iar corpul serverului rămâne în .details.
function extractSupportError(err: unknown): string {
  const candidates: unknown[] = [];

  if (axios.isAxiosError(err)) {
    candidates.push(err.response?.data);
  }

  if (err && typeof err === 'object') {
    const anyErr = err as Record<string, unknown>;
    // Formă ApiError produsă de handleApiError din api.ts
    if (anyErr.details) candidates.push(anyErr.details);
    candidates.push(anyErr);
  }

  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== 'object') continue;
    const data = candidate as Record<string, unknown>;
    if (typeof data.error === 'string' && data.error.trim()) {
      return data.error.trim();
    }
  }

  // Fallback pe mesajul generic (ApiError.message sau Error.message)
  if (err && typeof err === 'object') {
    const message = (err as Record<string, unknown>).message;
    if (typeof message === 'string' && message.trim()) {
      return message.trim();
    }
  }

  return 'Nu am putut trimite raportul de suport. Încercați din nou.';
}

// Validare locală, ca să nu trimitem degeaba fișiere prea mari.
// Serverul revalidează oricum tot.
function validatePayload(payload: BugReportPayload): void {
  const title = (payload.title || '').trim();
  const description = (payload.description || '').trim();

  if (!title) {
    throw new Error('Titlul este obligatoriu.');
  }
  if (/[\r\n]/.test(title)) {
    throw new Error('Titlul nu poate conține linii noi.');
  }
  if (title.length > SUPPORT_MAX_TITLE_LENGTH) {
    throw new Error(`Titlul poate avea maximum ${SUPPORT_MAX_TITLE_LENGTH} de caractere.`);
  }
  if (!description) {
    throw new Error('Descrierea este obligatorie.');
  }
  if (description.length > SUPPORT_MAX_DESCRIPTION_LENGTH) {
    throw new Error(`Descrierea poate avea maximum ${SUPPORT_MAX_DESCRIPTION_LENGTH} de caractere.`);
  }

  const files = payload.files || [];
  if (files.length > SUPPORT_MAX_FILES) {
    throw new Error(`Puteți atașa maximum ${SUPPORT_MAX_FILES} fișiere.`);
  }
  const tooBig = files.find((file) => file.size > SUPPORT_MAX_FILE_SIZE);
  if (tooBig) {
    throw new Error(`Fișierul „${tooBig.name}” depășește limita de 10 MB.`);
  }
}

// Trimite raportul de bug la backend, care îl transformă în issue Gitea.
// Identitatea utilizatorului și pagina raportată sunt completate de server
// din sesiune, respectiv din câmpurile page_* de mai jos.
export async function sendBugReport(payload: BugReportPayload): Promise<BugReportResult> {
  validatePayload(payload);

  const formData = new FormData();
  formData.append('title', payload.title.trim());
  formData.append('description', payload.description.trim());
  formData.append('page_url', payload.pageUrl || '');
  formData.append('page_path', payload.pagePath || '');
  formData.append('page_title', payload.pageTitle || '');

  if (payload.context && Object.keys(payload.context).length > 0) {
    formData.append('context', JSON.stringify(payload.context));
  }

  (payload.files || []).forEach((file) => {
    // Numele câmpului trebuie să fie `files` (upload.array('files', 5) pe server).
    formData.append('files', file);
  });

  try {
    // Nu setăm manual Content-Type: browserul adaugă boundary-ul corect.
    const response = await axios.post<BugReportResult>(
      `${getBaseUrl()}${BUG_REPORT_ENDPOINT}`,
      formData,
      {
        headers: {
          'Authorization': `Basic ${getAuthCredentials()}`,
        },
        // Peste bugetul maxim al serverului (15s creare issue + 40s atasamente),
        // ca sa nu abandonam un raport care de fapt s-a inregistrat.
        timeout: 90000,
      }
    );
    return response.data;
  } catch (err) {
    throw new Error(extractSupportError(err));
  }
}
